import { available_languages } from './globalConsts';

export { ukPhoneRegex, usPhoneRegex } from './globalConsts';

export const formatPhoneNumberDisplay = (
  phoneNumber: string | undefined,
  phoneNumberPrefix: string,
  isUs: boolean,
  isFormValue: boolean = false
) => {
  // Format phone numbers for display, remove any country code prefixs,
  //add a leading 0 if a UK number or format as (XXX) XXX-XXXX if a US number
  if (phoneNumber && phoneNumber.length >= 1) {
    const trimmedPhoneNumber = phoneNumber.slice(phoneNumberPrefix.length);    
    if (isUs) {
      return `(${trimmedPhoneNumber.slice(0, 3)}) ${trimmedPhoneNumber.slice(
        3,
        6
      )}-${trimmedPhoneNumber.slice(6, 10)}`;
    } else {
      return isFormValue ? '0' + trimmedPhoneNumber : '+44 (0)' + trimmedPhoneNumber;
    }
  }
  return isFormValue ? '' : '-';
};

export const formatPhoneNumberValue = (
  phoneNumber: string | undefined,
  phoneNumberPrefix: string,
): string => {
  // Format phone number values for API submission, remove any formatting characters,
  // ensure UK numbers have the correct country code prefix and no leading 0, and ensure US numbers have the correct country code prefix.
  if (!phoneNumber) return '';
  const cleanedNumber = phoneNumber.replace(/\D/g, '');
  if (cleanedNumber.length < 1) return cleanedNumber;
  const normalized = cleanedNumber.startsWith('0') ? cleanedNumber.slice(1) : cleanedNumber;
  return phoneNumberPrefix + normalized;
};

export const formatDate = (
  dateString: string | null | undefined,
  isUs: boolean = false
) => {
  const removeTimestamp = (dateString: string | null) => {
    return dateString?.split('T')[0];
  };
  if (!dateString) return null;
  const dateArr = dateString.includes('T')
    ? removeTimestamp(dateString)?.split('-')
    : dateString.split('-');
  if (dateArr && dateArr.length > 2) {
    if (isUs) {
      // returns MM/DD/YYYY format
      return `${dateArr[1]}/${dateArr[2]}/${dateArr[0]}`;
    } else {
      // returns DD/MM/YYYY format
      return dateArr.reverse().join('/');
    }
  }
  // fallback to the input (just in case!)
  return dateString;
};

export const formatTime = (dateTimeString: string | null | undefined) => {
  if (!dateTimeString) return null;
  const timeStr = dateTimeString?.split('T')[1];
  if (timeStr && timeStr.length > 5) {
    return timeStr.slice(0, 5);
  }
  // Return as is if it's shorter than 5 characters
  return timeStr;
};

export const formatDateTime = (dateString: string | null | undefined, isUs: boolean = false) => {
  if (!dateString) return null;  
  const formattedDate = formatDate(dateString, isUs);
  const formattedTime = formatTime(dateString);
  return formattedDate && formattedTime ? `${formattedDate}, ${formattedTime}` : formattedDate || formattedTime || null;
};

export const formatTimeWithTimeZone = (
  dateTimeString: string | null | undefined
) => {
  if (!dateTimeString) return null;

  // Extract the time part after the 'T' in the given datetime string
  const timeStr = dateTimeString.split('T')[1];
  // Split time and timezone offset (e.g., '12:34:56+01:00')
  const timeArr = timeStr?.toString().split('+');
  // Get the time in HH:MM format
  const time = timeStr && timeStr.length > 5 ? timeStr.slice(0, 5) : timeStr;
  // Get the timezone offset string (e.g., '01:00')
  const timeZoneString = timeArr?.length > 0 ? timeArr[1] : '';

  let timeZone = '';
  // Map known offsets to abbreviations
  if (timeZoneString === '01:00') {
    timeZone = 'BST';
  }
  if (timeZoneString === '00:00') {
    timeZone = 'GMT';
  }

  // Return formatted string if both time and timezone are present, otherwise just time
  return time && timeZone ? `${time}, ${timeZone}` : time || '';
};

export const formatAddressString = (addressString: string) => {
  /**  Because address's come formatted as `AddressLine1\nAddressLine2\nCity\nPostcode a straight 
	replace of `\n` can result in unwanted spaces, the additional regex check fixes this **/
  return addressString.replace(/[\r\n]+/g, ', ').replace(/,\s*$/, '');
};

export const convertToSnakeCase = (textString?: string | null) => {
  // Converts a string to snake_case and removes non-alphanumeric characters (except spaces)
  if (textString) {
    return textString
      .replace(/[^a-zA-Z0-9_ ]+/g, '') // Remove non-alphanumeric except spaces and underscores
      .replace(/([a-z])([A-Z])/g, '$1_$2') // Add underscore before uppercase letters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toLowerCase(); // Convert to lowercase
  } else {
    return '';
  }
};

export const formatLanguage = (lang: string) => {
  return available_languages[lang];
};

export const formatLongDateTime = (dateString?: string | null) => {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  } as const;
  if (dateString) {
    return new Date(dateString).toLocaleTimeString('en-UK', options);
  } else {
    return null;
  }
};

export const formatContactMethod = (contactMethod: string | unknown) => {
  switch (contactMethod) {
    case 'follow_up':
      return 'Follow up';
    case 'mobile_phone_call':
      return 'Mobile phone';
    case 'home_phone_call':
      return 'Home phone';
    case 'sms':
      return 'SMS';
    case 'email':
      return 'Email';
  }
  return '';
};

export const formatSnakeCase = (textString?: string | null) => {
  if (textString) {
    return textString.replace('_', ' ');
  } else {
    return null;
  }
};

export function formatToCapitalCase(str: string): string {
  if (!str) return '';
  // Replace underscores with spaces, then split into words
  return str
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
